import { EdgeFetchError } from '@nexus/ari-lib/api/supabase-edge-fetch';

import { uploadName, MAX_DOCUMENT_BYTES } from './documents-data';
import { wait, putEmpty, tusSend, tusCreate, tusOffset, TOKEN_RENEW_MS } from './documents-tus';
import { hashFile, sampledSha256 } from './documents-hash';
import { openUpload, completeUpload, heartbeatUpload } from './documents-api';

import type { UploadOutcome } from './documents-api-types';

export type UploadPhase = 'checking' | 'sending' | 'finishing';

export type UploadErrorCode =
  | 'too_big'
  | 'storage_full'
  | 'too_many_uploads'
  | 'folder_gone'
  | 'document_gone'
  | 'other_type'
  | 'other_file'
  | 'closed'
  | 'failed';

export type StoredUpload = { documentId: string; name: string; outcome: UploadOutcome };

export class UploadError extends Error {
  constructor(message: string, readonly code: UploadErrorCode) {
    super(message);
    this.name = 'UploadError';
  }
}

export type UploadCallbacks = {
  onPhase: (phase: UploadPhase) => void;
  /** 0 to 1 within the current phase. */
  onProgress: (fraction: number) => void;
  onSession: (sessionId: string) => void;
  /** Where storage keeps this upload's bytes, once it has a place for them. */
  onLocation?: (location: string) => void;
  /** Where an earlier attempt of the same upload left its bytes. A retry goes on from there. */
  resumeFrom?: string;
  /** The checksum an earlier attempt computed, so a resume does not read the whole file again. */
  checksum?: string;
  onChecksum?: (checksum: string) => void;
  /** The checksum of the unfinished upload this file resumes. Another file is refused. */
  expectedChecksum?: string;
  /** All bytes may be in storage already, so completion is tried before anything is sent. CLOUD-1107. */
  completeFirst?: boolean;
  signal: AbortSignal;
  retryDelaysMs?: number[];
  completeDelaysMs?: number[];
  heartbeatMs?: number;
};

/** A completion that asked for the bytes again. */
const RESEND_ATTEMPTS = 2;
/** A completion lost on the network is asked again after these waits. CLOUD-1107. */
const COMPLETE_DELAYS_MS = [2000, 5000, 15000];
/** The server counts an upload as stopped after five minutes without one. */
export const HEARTBEAT_MS = 60 * 1000;

type CompletionFailure = 'again' | 'resend' | 'mismatch' | 'final';

function completionFailure(error: unknown): CompletionFailure {
  if (error instanceof EdgeFetchError) {
    if (error.code === 'mismatch') return 'mismatch';
    // A stalled upload whose room another upload took. Waiting does not bring it back.
    if (error.status === 507) return 'final';
    if (error.status === 409) return error.code === 'closed' || error.code === 'other_type' ? 'final' : 'resend';
    return error.status >= 500 ? 'again' : 'final';
  }
  // fetch fails with a TypeError when the network drops, such as "Failed to fetch".
  return error instanceof TypeError ? 'again' : 'final';
}

/** What a completion that will not succeed throws: storage full as the tray knows it, else the error itself. */
function completionError(error: unknown): unknown {
  return error instanceof EdgeFetchError && error.status === 507 ? openFailure(error) : error;
}

function openFailure(error: unknown): UploadError {
  if (error instanceof EdgeFetchError && error.status === 507) {
    return new UploadError('Storage is full.', 'storage_full');
  }
  if (error instanceof EdgeFetchError && error.status === 429) {
    return new UploadError(
      'Too many uploads are open. Close the upload list to cancel the failed ones, or try again in a few hours.',
      'too_many_uploads'
    );
  }
  if (error instanceof EdgeFetchError && error.code === 'no_document') {
    return new UploadError('The file is gone.', 'document_gone');
  }
  if (error instanceof EdgeFetchError && error.code === 'other_type') {
    return new UploadError('A replacement must be the same type of file.', 'other_type');
  }
  if (error instanceof EdgeFetchError && error.status === 404) {
    return new UploadError('The folder is gone.', 'folder_gone');
  }
  if (error instanceof EdgeFetchError && error.code === 'closed') {
    return new UploadError('The upload was closed. Try again.', 'closed');
  }
  return new UploadError(error instanceof Error ? error.message : 'Upload failed.', 'failed');
}

/** Tells the server the upload is alive. Stops the upload when the server no longer keeps room for it. */
function keepAlive(
  sessionId: string,
  state: () => { resumeLocation?: string; sentBytes: number },
  stop: (reason: UploadError) => void,
  everyMs: number
) {
  let last = 0;
  const beat = () => {
    last = Date.now();
    heartbeatUpload(sessionId, state()).catch((error: unknown) => {
      if (error instanceof EdgeFetchError && (error.status === 507 || error.code === 'closed'))
        stop(openFailure(error));
    });
  };
  const timer = setInterval(beat, everyMs);
  // A progress report beats early now and then, so a stopped upload shows how far it got.
  const nudge = () => {
    if (Date.now() - last >= everyMs / 4) beat();
  };
  return { beat, nudge, end: () => clearInterval(timer) };
}

/** Returns the name the file was stored under, which can differ from `file.name`. */
export async function uploadDocument(
  file: File,
  folderId: string | null,
  clientUploadId: string,
  callbacks: UploadCallbacks,
  replaceDocumentId?: string
): Promise<StoredUpload> {
  const { signal } = callbacks;
  if (file.size > MAX_DOCUMENT_BYTES) throw new UploadError('This file is larger than 10 GB.', 'too_big');

  // The heartbeat can stop the upload too, so everything below listens to this signal.
  const inner = new AbortController();
  const passOn = () => inner.abort();
  signal.addEventListener('abort', passOn, { once: true });
  let stopped: UploadError | null = null;
  const stop = (reason: UploadError) => {
    stopped ??= reason;
    inner.abort();
  };
  const alive = { sentBytes: 0, resumeLocation: undefined as string | undefined };
  const heartbeat: { current: ReturnType<typeof keepAlive> | null } = { current: null };
  // A pause releases the room. A heartbeat after it would claim the room again, even while a
  // completion that does not listen to the signal is still open.
  inner.signal.addEventListener('abort', () => heartbeat.current?.end(), { once: true });

  try {
    return await upload(file, folderId, clientUploadId, callbacks, replaceDocumentId, {
      signal: inner.signal,
      alive,
      onSession: (sessionId) => {
        heartbeat.current = keepAlive(sessionId, () => alive, stop, callbacks.heartbeatMs ?? HEARTBEAT_MS);
      },
      onLocation: () => heartbeat.current?.beat(),
      onSent: () => heartbeat.current?.nudge(),
    });
  } catch (error) {
    throw stopped ?? error;
  } finally {
    heartbeat.current?.end();
    signal.removeEventListener('abort', passOn);
  }
}

type Inner = {
  signal: AbortSignal;
  alive: { sentBytes: number; resumeLocation?: string };
  onSession: (sessionId: string) => void;
  onLocation: () => void;
  onSent: () => void;
};

async function upload(
  file: File,
  folderId: string | null,
  clientUploadId: string,
  callbacks: UploadCallbacks,
  replaceDocumentId: string | undefined,
  { signal, alive, onSession, onLocation, onSent }: Inner
): Promise<StoredUpload> {
  const { onPhase, onProgress, resumeFrom, retryDelaysMs } = callbacks;

  onPhase('checking');
  const fraction = (bytes: number) => (file.size ? bytes / file.size : 1);
  const checksumSha256 = callbacks.checksum ?? (await hashFile(file, (bytes) => onProgress(fraction(bytes)), signal));
  if (callbacks.expectedChecksum && checksumSha256 !== callbacks.expectedChecksum) {
    throw new UploadError('This is not the file you started to upload.', 'other_file');
  }
  callbacks.onChecksum?.(checksumSha256);
  const sampled = await sampledSha256(file);
  const mimeType = file.type || 'application/octet-stream';

  // The same client id finds the same session again, with a fresh upload token.
  const open = () =>
    openUpload({
      clientUploadId,
      folderId,
      name: uploadName(file.name),
      mimeType,
      byteSize: file.size,
      checksumSha256,
      sampledSha256: sampled,
      ...(replaceDocumentId ? { replaceDocumentId } : {}),
    }).catch((error: unknown) => {
      throw openFailure(error);
    });

  let opened = await open();
  if (opened.status === 'already_complete') {
    return { documentId: opened.documentId, name: uploadName(file.name), outcome: 'stored' };
  }
  callbacks.onSession(opened.sessionId);
  // Paused or cancelled while the session opened. Nothing more is sent.
  signal.throwIfAborted();
  onSession(opened.sessionId);
  const { sessionId, name } = opened;
  const stored = (completed: Awaited<ReturnType<typeof completeUpload>>): StoredUpload => ({
    documentId: completed.documentId,
    name: completed.name ?? name,
    outcome: completed.outcome ?? 'stored',
  });

  // The cloud threw damaged bytes away, so there is nothing to resume from.
  let damaged = false;
  if (callbacks.completeFirst && file.size > 0) {
    onPhase('finishing');
    try {
      return stored(await completeUpload(sessionId));
    } catch (error) {
      const failure = completionFailure(error);
      if (failure === 'final') throw completionError(error);
      if (failure === 'mismatch') {
        // As in the loop below: a new upload sends all of the bytes again.
        opened = await open();
        if (opened.status === 'already_complete') {
          return { documentId: opened.documentId, name: uploadName(file.name), outcome: 'stored' };
        }
        damaged = true;
      }
    }
  }

  // A token lasts two hours. Opening the same upload again signs a new one for the same place.
  let token = { value: opened.token, at: Date.now() };
  const validToken = async () => {
    if (Date.now() - token.at >= TOKEN_RENEW_MS) {
      const again = await open();
      if (again.status === 'ready') token = { value: again.token, at: Date.now() };
    }
    return token.value;
  };

  const progress = (bytes: number) => {
    alive.sentBytes = bytes;
    onSent();
    onProgress(fraction(bytes));
  };
  const placed = (location: string) => {
    alive.resumeLocation = location;
    callbacks.onLocation?.(location);
    onLocation();
  };
  const send = { onProgress: progress, signal, retryDelaysMs };
  const sendAll = async (session: { bucket: string; path: string; token: string }) => {
    onPhase('sending');
    token = { value: session.token, at: Date.now() };
    const target = { bucket: session.bucket, path: session.path, token: session.token, contentType: mimeType };
    if (file.size === 0) {
      await putEmpty(target, signal);
      onPhase('finishing');
      return '';
    }
    const created = await tusCreate(target, file.size, signal);
    placed(created);
    await tusSend(created, file, validToken, send);
    onPhase('finishing');
    return created;
  };
  // A retry goes on from the bytes storage already has. Only when those are gone does it start again.
  let location: string;
  if (resumeFrom && !damaged && (await tusOffset(resumeFrom, await validToken(), signal)) !== null) {
    onPhase('sending');
    location = resumeFrom;
    placed(location);
    await tusSend(location, file, validToken, send, true);
    onPhase('finishing');
  } else {
    location = await sendAll(opened);
  }

  const delays = callbacks.completeDelaysMs ?? COMPLETE_DELAYS_MS;
  let waits = 0;
  let resends = 0;
  for (;;) {
    try {
      return stored(await completeUpload(sessionId));
    } catch (error) {
      const failure = completionFailure(error);
      if (failure === 'again' && waits < delays.length) {
        await wait(delays[waits], signal);
        waits += 1;
        continue;
      }
      if (failure === 'final' || failure === 'again' || resends >= RESEND_ATTEMPTS) throw completionError(error);
      resends += 1;
      if (failure === 'mismatch') {
        // The cloud threw the damaged bytes away. A new upload sends all of them again.
        opened = await open();
        if (opened.status === 'already_complete') {
          return { documentId: opened.documentId, name: uploadName(file.name), outcome: 'stored' };
        }
        location = await sendAll(opened);
      } else if (file.size > 0) {
        await tusSend(location, file, validToken, send, true);
      }
    }
  }
}
